import React, { useCallback, useEffect, useState } from "react";
import api from "../lib/api";
import { Plus, Save, Trash2, Tag, DollarSign, AlertTriangle } from "lucide-react";

const empty = () => ({
  id: null,
  key: "",
  name: "",
  description: "",
  audience: "student",
  price_amount_cents: 9700,
  currency: "BRL",
  stripe_lookup_key: "",
  active: true,
});

function PlanRow({ plan, onChange, onSave, onDelete, saving }) {
  return (
    <div
      data-testid={`plan-row-${plan.key || "new"}`}
      className={`rounded-xl border p-3 sm:p-4 space-y-2 ${
        plan.audience === "mentor" ? "border-purple-500/30 bg-purple-500/5" : "border-[#282D3D] bg-[#14161C]"
      }`}
    >
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest font-extrabold">
          <Tag className="w-3.5 h-3.5 text-[#FF6B00]" />
          <span className={plan.audience === "mentor" ? "text-purple-300" : "text-[#FF6B00]"}>
            {plan.audience === "mentor" ? "Plano Mentor" : "Plano Aluno"}
          </span>
        </div>
        <div className="flex items-center gap-1">
          <button
            data-testid={`plan-save-${plan.key || "new"}`}
            onClick={() => onSave(plan)}
            disabled={saving}
            className="p-1.5 rounded-md bg-[#FF6B00] hover:bg-[#FF8533] text-black text-xs font-extrabold disabled:opacity-60"
            title="Salvar"
          >
            <Save className="w-3.5 h-3.5" />
          </button>
          {plan.id && (
            <button
              data-testid={`plan-delete-${plan.key}`}
              onClick={() => onDelete(plan)}
              className="p-1.5 rounded-md bg-red-500/10 text-red-400 hover:bg-red-500/20"
              title="Remover"
            >
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
        <label className="block">
          <span className="text-[10px] uppercase font-bold text-gray-400">Chave</span>
          <input
            data-testid={`plan-key-${plan.key || "new"}`}
            value={plan.key}
            onChange={(e) => onChange({ ...plan, key: e.target.value })}
            className="w-full bg-[#0F1219] border border-[#282D3D] rounded-lg py-1.5 px-2 text-white text-xs"
          />
        </label>
        <label className="block sm:col-span-2">
          <span className="text-[10px] uppercase font-bold text-gray-400">Nome</span>
          <input
            data-testid={`plan-name-${plan.key || "new"}`}
            value={plan.name}
            onChange={(e) => onChange({ ...plan, name: e.target.value })}
            className="w-full bg-[#0F1219] border border-[#282D3D] rounded-lg py-1.5 px-2 text-white text-xs"
          />
        </label>
      </div>

      <label className="block">
        <span className="text-[10px] uppercase font-bold text-gray-400">Descrição</span>
        <textarea
          rows={2}
          value={plan.description || ""}
          onChange={(e) => onChange({ ...plan, description: e.target.value })}
          className="w-full bg-[#0F1219] border border-[#282D3D] rounded-lg py-1.5 px-2 text-white text-xs resize-y"
        />
      </label>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 items-end">
        <label className="block">
          <span className="text-[10px] uppercase font-bold text-gray-400">Público</span>
          <select
            data-testid={`plan-audience-${plan.key || "new"}`}
            value={plan.audience}
            onChange={(e) => onChange({ ...plan, audience: e.target.value })}
            className="w-full bg-[#0F1219] border border-[#282D3D] rounded-lg py-1.5 px-2 text-white text-xs"
          >
            <option value="student">Aluno</option>
            <option value="mentor">Mentor</option>
          </select>
        </label>
        <label className="block">
          <span className="text-[10px] uppercase font-bold text-gray-400">Moeda</span>
          <input
            value={plan.currency}
            maxLength={3}
            onChange={(e) => onChange({ ...plan, currency: e.target.value.toUpperCase() })}
            className="w-full bg-[#0F1219] border border-[#282D3D] rounded-lg py-1.5 px-2 text-white text-xs uppercase"
          />
        </label>
        <label className="block">
          <span className="text-[10px] uppercase font-bold text-gray-400">Valor (centavos)</span>
          <input
            type="number"
            min={0}
            data-testid={`plan-price-${plan.key || "new"}`}
            value={plan.price_amount_cents}
            onChange={(e) => onChange({ ...plan, price_amount_cents: parseInt(e.target.value || 0, 10) })}
            className="w-full bg-[#0F1219] border border-[#282D3D] rounded-lg py-1.5 px-2 text-white text-xs"
          />
        </label>
        <label className="flex items-center gap-1.5 text-[11px] text-gray-300">
          <input
            type="checkbox"
            checked={plan.active !== false}
            onChange={(e) => onChange({ ...plan, active: e.target.checked })}
            className="accent-[#FF6B00]"
          />
          <span>Ativo</span>
        </label>
      </div>

      <label className="block">
        <span className="text-[10px] uppercase font-bold text-gray-400">Stripe lookup_key</span>
        <input
          data-testid={`plan-stripe-${plan.key || "new"}`}
          value={plan.stripe_lookup_key || ""}
          placeholder="modo_caverna_student_monthly"
          onChange={(e) => onChange({ ...plan, stripe_lookup_key: e.target.value })}
          className="w-full bg-[#0F1219] border border-[#282D3D] rounded-lg py-1.5 px-2 text-white text-xs font-mono"
        />
      </label>
    </div>
  );
}

export default function PlansEditor() {
  const [plans, setPlans] = useState([]);
  const [drafts, setDrafts] = useState({});
  const [newPlan, setNewPlan] = useState(empty());
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await api.get("/api/admin/plans");
      const list = res.data.plans || [];
      setPlans(list);
      const drafts = {};
      list.forEach((p) => (drafts[p.id] = { ...p }));
      setDrafts(drafts);
    } catch (err) {
      setError(err?.response?.data?.detail || "Erro ao carregar planos.");
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleSave = async (plan) => {
    setError("");
    setMessage("");
    setSaving(true);
    try {
      const payload = {
        key: plan.key,
        name: plan.name,
        description: plan.description || null,
        audience: plan.audience,
        price_amount_cents: plan.price_amount_cents,
        currency: plan.currency,
        stripe_lookup_key: plan.stripe_lookup_key || null,
        active: plan.active !== false,
      };
      if (plan.id) {
        await api.put(`/api/admin/plans/${plan.id}`, payload);
      } else {
        await api.post("/api/admin/plans", payload);
        setNewPlan(empty());
      }
      setMessage("Plano salvo com sucesso.");
      await load();
    } catch (err) {
      setError(err?.response?.data?.detail || "Erro ao salvar plano.");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (plan) => {
    if (!window.confirm("Remover este plano?")) return;
    try {
      await api.delete(`/api/admin/plans/${plan.id}`);
      await load();
    } catch (err) {
      setError(err?.response?.data?.detail || "Erro ao remover.");
    }
  };

  return (
    <div data-testid="plans-editor" className="space-y-3">
      <div className="bg-[#14161C] border border-[#282D3D] rounded-xl p-4">
        <div className="flex items-center gap-2 text-xs font-bold text-[#FF6B00] uppercase tracking-wider mb-1">
          <DollarSign className="w-4 h-4" />
          <span>Planos de Assinatura</span>
        </div>
        <p className="text-xs text-gray-400">
          Configure os planos exibidos no cadastro. O <code>stripe_lookup_key</code> deve
          corresponder ao Price criado no Stripe (test/live).
        </p>
      </div>

      {error && (
        <div className="rounded-lg bg-red-500/10 border border-red-500/40 text-red-300 text-xs p-3 flex items-start gap-2">
          <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" /> <span>{error}</span>
        </div>
      )}
      {message && (
        <div className="rounded-lg bg-emerald-500/10 border border-emerald-500/40 text-emerald-300 text-xs p-3">
          {message}
        </div>
      )}

      {plans.map((plan) => (
        <PlanRow
          key={plan.id}
          plan={drafts[plan.id] || plan}
          onChange={(p) => setDrafts((d) => ({ ...d, [plan.id]: p }))}
          onSave={handleSave}
          onDelete={handleDelete}
          saving={saving}
        />
      ))}

      <div className="rounded-xl border border-dashed border-[#3E4760] bg-[#0F1219] p-3 sm:p-4">
        <div className="flex items-center gap-2 mb-2 text-xs font-bold text-gray-300 uppercase">
          <Plus className="w-4 h-4 text-[#FF6B00]" />
          <span>Adicionar novo plano</span>
        </div>
        <PlanRow
          plan={newPlan}
          onChange={setNewPlan}
          onSave={handleSave}
          onDelete={() => {}}
          saving={saving}
        />
      </div>
    </div>
  );
}
